// There is exactly ONE selection value in this app:
//
//   null
//   | { type: "sephirah", slug: "tiferet" }
//   | { type: "path", slug: "samekh" }
//   | { type: "marker", slug: "ain_soph_aur" }
//
// The third is an overlay group on the marker channel — the three negative
// veils and the three horizontal thresholds. It is the same shape as the other
// two on purpose: a marker is another thing on the diagram you can point at and
// read about, so it goes through the one selection value rather than growing a
// second one beside it.
//
// Two variables (selectedSephirah + selectedPath) would mean two sources of
// truth, two ways to be "selected", and a state where both are set at once.
//
// Identity is the SLUG, not the number, because the slug is what survives a
// change of tradition: Samekh is Samekh on both trees even though its number
// differs. It is also what the URL carries.

export function isSelected(selected, type, slug) {
  return Boolean(selected) && selected.type === type && selected.slug === slug;
}

// Does this path touch the selected sphere?
export function pathTouches(path, selected) {
  if (!selected || selected.type !== "sephirah") return false;
  return path.fromSlug === selected.slug || path.toSlug === selected.slug;
}

// Is this sphere an endpoint of the selected path?
export function isEndpointOf(sephirah, selected, paths) {
  if (!selected || selected.type !== "path") return false;
  const path = paths.find((p) => p.slug === selected.slug);
  if (!path) return false;
  return path.fromSlug === sephirah.slug || path.toSlug === sephirah.slug;
}

// The href a tradition switch should navigate to. The selection is preserved by
// slug where the target tradition has a counterpart, and CLEARED where it does
// not — never guessed at.
export function traditionHref(target, selected) {
  const base = `/tree/${target.param}`;
  if (!selected) return base;
  // A marker belongs to a lens, and a lens with no counterpart in the target
  // tradition is dropped rather than guessed at. The same rule, one level down.
  if (selected.type === "marker") return base;

  const slugs =
    selected.type === "sephirah" ? target.sephirahSlugs : target.pathSlugs;
  if (!slugs || !slugs.includes(selected.slug)) return base;

  const segment = selected.type === "sephirah" ? "sephiroth" : "paths";
  return `${base}/${segment}/${selected.slug}`;
}
